import { useLocation, useNavigate } from "react-router-dom";
import { useDictStore } from "@/shared/models/dict";
import { useState, useEffect, useMemo, useRef, useCallback, Suspense } from "react";
import { useAuth } from "@/shared/libs/auth";
import { useNavigation } from "@/shared/libs/navigation";
import { useKeepAlive } from "@/shared/libs/keepAlive";
import { useTitle, useToast } from "@/shared/hooks";

import { Alert } from "@/features/Alert";
import { Header, Content, PasswordChange } from "./components";
import { ERPRouter } from "./routes";
import { useNotification } from "@/components/Notification/NotificationManager";
import { notificationApi } from "@/service/flow/notification";
import { WebSocketNoticeData } from "@/shared/hooks/useWebSocket";

export const ERPLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { success: originalSuccess } = useToast({ timeout: 3000 });
  const success = useCallback(originalSuccess, []); // 稳定 success

  const initlize = useDictStore((state) => state.initlize);
  const { user, logout } = useAuth();
  const { navList } = useNavigation();
  const { push } = useKeepAlive();
  const setTitle = useTitle();
  const [showPwdChange, setShowPwdChange] = useState(false);

  // --- 通知轮询相关状态 ---
  const [unreadCount, setUnreadCount] = useState(0);
  const shownNotificationIds = useRef<Set<string>>(new Set());
  const { showNotification: originalShowNotification } = useNotification();
  const showNotification = useCallback(originalShowNotification, []); // 稳定 showNotification

  // 使用递归 setTimeout 实现轮询
  useEffect(() => {
    if (!user?.id) return;

    let timeoutId: NodeJS.Timeout;

    const fetchNotifications = async () => {
      try {
        const notifications = await notificationApi.findToMeNotification(user.id);
        // console.log("notifications:", notifications);

        setUnreadCount(notifications?.length ?? 0);

        notifications.forEach((notif: WebSocketNoticeData) => {
          // if (shownNotificationIds.current.has(notif.id)) return;

          // 根据优先级确定通知类型和持续时间
          let notificationType: 'success' | 'warning' | 'error' | 'info' = 'info';
          let durationSeconds = 5;
          if (notif.priority === 'URGENT') {
            notificationType = 'error';
            durationSeconds = 15;
          } else if (notif.priority === 'HIGH') {
            notificationType = 'warning';
            durationSeconds = 10;
          } else if (notif.priority === 'MEDIUM') {
            notificationType = 'success';
            durationSeconds = 5;
          } else {
            notificationType = 'info';
            durationSeconds = 3;
          }

          let notificationTitle = '系统通知';
          if (notif.notificationType === 'SYSTEM') {
            notificationTitle = '系统通知';
          } else if (notif.notificationType === 'TASK') {
            notificationTitle = '任务通知';
          } else if (notif.notificationType === 'APPROVAL') {
            notificationTitle = '审批通知';
          } else {
            notificationTitle = '提及通知';
          }

          // shownNotificationIds.current.add(notif.id);
        // console.log("============= 提示框展示 =========")
          showNotification({
            type: notificationType,
            title: notificationTitle,
            message: notif.content ?? '无',
            duration: durationSeconds * 1000,
            onImmediateHandle: () => {
              notificationApi.confirmNotification(notif.id).catch(() => {
                success("通知详情，请前往协同办公->我的代办进行查看");
              });
            },
            onLaterHandle: () => {
              console.log('用户选择稍后处理');
              notificationApi.readNotification(notif.id).catch(error => {
                console.log("阅读失败!!", error);
              });
            },
          });
        });
      } catch (error) {
        console.error('轮询通知失败:', error);
      } finally {
        // 无论成功或失败，都安排下一次轮询（30秒后）
        timeoutId = setTimeout(fetchNotifications, 60000);
        // timeoutId = setTimeout(fetchNotifications, 3000);
      }
    };

    // 立即执行第一次
    fetchNotifications();

    return () => {
      clearTimeout(timeoutId);
      shownNotificationIds.current.clear(); // 清理记录
    };
  }, [user?.id, showNotification, success]); // 依赖稳定函数，不会导致频繁重建

  // --- 原有初始化代码 ---
  useEffect(() => {
    initlize();
  }, [initlize]);

  const currentTopNav = useMemo(() => {
    const path = location.pathname.split("/")[2];
    return navList?.find((nav) => nav.path === path);
  }, [navList, location]);

  useEffect(() => {
    const paths = location.pathname.split("/").slice(2);
    const current = paths
        .slice(1)
        .reduce<ArrayExtract<typeof navList> | undefined>(
            (prev, path) => {
              return prev?.children.find((i) => i.path === path);
            },
            navList?.find((item) => item.path === paths[0])
        );

    if (current) {
      push({
        active: true,
        description: current.remark,
        name: current.name,
        key: current.path,
        title: current.name,
      });
      setTitle(current.name);
    }
  }, [location.pathname, navList, push, setTitle]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        const target = event.target as HTMLElement;
        if (target.nodeName === "INPUT") {
          event.preventDefault();
        }
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, []);

  return (
      <div className="h-[100vh] min-w-[1280px] w-full flex flex-col">
        <Header
            user={user!}
            noticeNum={unreadCount}
            navList={navList ?? []}
            handleChangePassword={setShowPwdChange.bind(undefined, true)}
            hanldeLogOut={logout}
        />
        <Content currentNav={currentTopNav}>
          <Suspense>
            <ERPRouter navList={navList ?? []} />
          </Suspense>
        </Content>
        <PasswordChange
            isOpen={showPwdChange}
            onClose={() => setShowPwdChange(false)}
            onSuccess={() => {
              setShowPwdChange(false);
              logout();
            }}
        />
        <Alert />
      </div>
  );
};
